import { action, computed, extendObservable, makeObservable } from 'mobx'
import { BlockModel } from '@client/components/Block/model'
import {
  SOCIAL_SERVICE_NAMES,
  SOCIAL_SERVICE_SHARE_LINKS,
  defaultSettings,
  SOCIAL_SERVICE_DISABLE_FOLLOW,
} from './constants'
import { BLOCK_TYPES } from '@client/components/Block/constants'

const INITIAL_COUNT_OF_SERVICES = 4

export default class SocialModel extends BlockModel {
  constructor(data = {}, editor) {
    super(data)
    makeObservable(this)

    this.editor = editor
    this.settings.set({ ...defaultSettings, ...data.settings })

    extendObservable(this, {
      services: data.services || [],
    })

    if (!this.services.length) {
      this.setInitialServices(INITIAL_COUNT_OF_SERVICES)
    }
  }

  setInitialServices = (count) => {
    for (let i = 0; i < count; i += 1) {
      this.addService()
    }
  }

  @action addService = () => {
    const name = this.firstVacantName
    // A nameless service would render nothing and still persist, which is the
    // shape of bad data FE-1348 was about. Do nothing instead.
    if (!name) {
      return
    }

    this.services = [
      ...this.services,
      {
        name,
        link: '',
      },
    ]
  }

  @action removeService = (name) => {
    this.services = this.services.filter((item) => item.name !== name)
  }

  @action setServiceLink = (name, link) => {
    this.services = this.services.map((item) => {
      if (item.name !== name) {
        return item
      }
      return {
        ...item,
        link: link,
      }
    })
  }

  // Counted against `servicesAsOptions` in `servicesLeft`, so it has to be
  // drawn from the same population. `servicesAsOptions` iterates
  // `SOCIAL_SERVICE_NAMES`, so a stored name absent from that map can never
  // appear there; counting one here made `servicesLeft` go negative, which
  // *enables* the Add button (`!(-1)` is `false`) at the exact moment nothing
  // is left to add, and `firstVacantName` then found no vacant option
  // (FE-1348). Deriving from `filteredServices` applies the same unknown-name
  // guard and drops a predicate that was duplicated from it.
  @computed get selectedServices() {
    return this.filteredServices.map((item) => item.name)
  }

  @computed get servicesAsOptions() {
    return Object.keys(SOCIAL_SERVICE_NAMES)
      .filter((key) => {
        const isTypeShare = this.type === BLOCK_TYPES.SHARE_V2
        return (
          (isTypeShare && SOCIAL_SERVICE_SHARE_LINKS[key]) ||
          (!isTypeShare && !SOCIAL_SERVICE_DISABLE_FOLLOW[key])
        )
      })
      .map((key) => ({
        disabled: this.selectedServices.includes(key),
        name: SOCIAL_SERVICE_NAMES[key],
        value: key,
      }))
  }

  // Measured from the options themselves rather than by subtracting two
  // counts. The subtraction only equalled the real vacancy while
  // `selectedServices` held no duplicates, and nothing enforces that — a
  // stored body can repeat a name. That made this negative, and the Add button
  // is gated on `!servicesLeft`, so `!(-1)` re-enabled it with nothing left to
  // add (FE-1348).
  @computed get servicesLeft() {
    return this.servicesAsOptions.filter((item) => !item.disabled).length
  }

  // `undefined` when every option is taken. `servicesLeft` gates the Add
  // button, but it is a count and a stored body can repeat a name, so the two
  // can disagree — this must not throw when they do.
  @computed get firstVacantName() {
    return this.servicesAsOptions.find((item) => !item.disabled)?.value
  }

  // What persists. Deliberately *not* `filteredServices`: a name this map
  // doesn't know cannot be rendered (FE-1348), but the stored link is the
  // user's data and map membership is not stable enough to delete rows on —
  // `GOOGLE_PLUS` left the V1 map in 2019 and the V2 map has gained six names
  // since. Dropping such a row here would make merely opening and saving an
  // old mail destroy a link the user never chose to remove, and would do it
  // silently, because an unrecognised service is not shown in the editor
  // either. So this applies only the exclusions that predate FE-1348, leaving
  // a save exactly as lossless as it has always been.
  @computed get serializableServices() {
    const isTypeShare = this.type === BLOCK_TYPES.SHARE_V2
    return this.services.filter(
      (s) =>
        (isTypeShare && SOCIAL_SERVICE_SHARE_LINKS[s.name]) ||
        (!isTypeShare && !SOCIAL_SERVICE_DISABLE_FOLLOW[s.name])
    )
  }

  // What renders, and what the editor and link checker walk. A stored name
  // this map doesn't know feeds `undefined` into `formatMessage`, which throws
  // and, server-side, takes the whole renderer process down (FE-1348); it also
  // builds an icon URL of `.../undefined.png`. This map has only ever gained
  // keys, so no stale name is known to reach it — unlike the V1 map, which
  // dropped `GOOGLE_PLUS` in 2019. The guard is here because nothing stops a
  // stored body from carrying an unknown name and the cost of one is the whole
  // process.
  @computed get filteredServices() {
    return this.serializableServices.filter((s) => SOCIAL_SERVICE_NAMES[s.name])
  }

  serialize = () => {
    return {
      type: this.type,
      services: this.serializableServices,
      settings: this.settings.values,
    }
  }

  restore = (data) => {
    this.settings.set(data.settings)
    this.services = data.services
  }
}

export const NULL = new SocialModel()
